import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, XCircle } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import { MONTHS, money, rateLabel } from "./returnUi";
import { buildQapDat, downloadQapDat, qapPreviewRows } from "../../utils/qapDat";
import { downloadQapPreviewCsv } from "../../utils/qapPreviewCsv";
import { QAP_LAYOUT_NOTE } from "../../utils/qapLayout";

// QAP (DAT) tab — the 1601-EQ Quarterly Alphalist of Payees as a BIR DAT file
// (docs/WHT_IMPLEMENTATION.md §9, DAT-B). The record layout is PROVISIONAL
// (utils/qapLayout.js) — the banner says so on every view. Download is
// disabled while any pre-export check fails.

const quarterMonths = (q) => [q * 3 - 2, q * 3 - 1, q * 3].map((m) => MONTHS[m - 1]).join("–");
const tinLabel = (t) => (t ? `${t.slice(0, 3)}-${t.slice(3, 6)}-${t.slice(6, 9)}` : "—");

const defaultQuarter = (year) => {
  const now = new Date();
  return year === now.getFullYear() ? Math.ceil((now.getMonth() + 1) / 3) : 4;
};

const CheckRow = ({ check }) => (
  <li className="py-2 border-b border-ink-line last:border-b-0" data-check={check.code}>
    <div className="flex items-start gap-2 text-sm">
      {check.ok
        ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-success-600 shrink-0" aria-label="passed" />
        : <XCircle className="w-4 h-4 mt-0.5 text-danger-600 shrink-0" aria-label="failed" />}
      <div className="min-w-0">
        <div className="text-ink">{check.label}</div>
        {!check.ok && check.message && <div className="text-danger-700 text-xs mt-0.5">{check.message}</div>}
        {!check.ok && check.rows?.length > 0 && (
          <table className="mt-2 text-xs w-full" aria-label={`Rows — ${check.label}`}>
            <thead>
              <tr className="text-ink-body">
                <th className="text-left pr-3 font-medium">Month</th>
                <th className="text-left pr-3 font-medium">Payee</th>
                <th className="text-left pr-3 font-medium">ATC</th>
                <th className="text-right pr-3 font-medium">Tax base</th>
                <th className="text-right font-medium">Tax withheld</th>
              </tr>
            </thead>
            <tbody>
              {check.rows.map((r) => (
                <tr key={r._id}>
                  <td className="pr-3 text-ink">{MONTHS[r.month - 1]}</td>
                  <td className="pr-3 text-ink">{r.counterpartyName || "—"}</td>
                  <td className="pr-3 text-ink">{r.atcCode || "—"}</td>
                  <td className="pr-3 text-right tabular-nums text-ink">{money(r.baseCentavos)}</td>
                  <td className="text-right tabular-nums text-ink">{money(r.taxWithheldCentavos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </li>
);

const QapDatTab = ({ client, year, onGoToEwtRecords }) => {
  const clientId = client?._id;
  const [quarter, setQuarter] = useState(() => defaultQuarter(year));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!clientId || !year || !quarter) return;
    setLoading(true);
    setError(null);
    try {
      setData((await withholdingTaxAPI.getQap(clientId, year, quarter)).data);
    } catch (e) {
      setData(null);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, year, quarter]);

  useEffect(() => { load(); }, [load]);

  const built = useMemo(() => (data ? buildQapDat(data) : null), [data]);
  const rows = useMemo(() => qapPreviewRows(data), [data]);
  const eq = data?.return1601eq;
  const item19 = eq?.item19Centavos;
  const totalWithheld = data?.control?.totalTaxWithheldCentavos ?? 0;
  const ties = item19 !== null && item19 !== undefined && item19 === totalWithheld;
  const failing = (data?.checks || []).filter((c) => !c.ok);
  const hasRowProblems = failing.some((c) => c.rows?.length);

  return (
    <div>
      <div
        role="note"
        aria-label="Provisional format"
        className="flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 px-3 py-2 mb-4 text-sm text-warning-900"
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-warning-700" />
        <span>{QAP_LAYOUT_NOTE}</span>
      </div>

      <div role="group" aria-label="Quarter" className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {[1, 2, 3, 4].map((q) => (
          <button
            key={q}
            type="button"
            aria-pressed={quarter === q}
            onClick={() => setQuarter(q)}
            className={`rounded-md border px-3 py-2 text-left text-sm ${
              quarter === q ? "border-primary-500 bg-primary-50" : "border-ink-line bg-white"
            }`}
          >
            <span className="font-medium text-ink">Q{q} <span className="font-normal text-ink-body">· {quarterMonths(q)}</span></span>
          </button>
        ))}
      </div>

      {error && <div role="alert" className="mb-4 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</div>}
      {loading && !data && <p className="text-sm text-ink-body">Loading…</p>}

      {data && (
        <>
          <section className="border border-ink-line rounded-lg bg-white p-4 mb-4" aria-label="Totals">
            <h4 className="text-sm font-medium text-ink mb-2">Totals — Q{quarter} {year}</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <dt className="text-ink-body">DAT total tax base</dt>
                <dd className="tabular-nums text-ink" data-testid="qap-total-base">{money(data.control.totalTaxBaseCentavos)}</dd>
              </div>
              <div>
                <dt className="text-ink-body">DAT total tax withheld</dt>
                <dd className="tabular-nums text-ink" data-testid="qap-total-withheld">{money(totalWithheld)}</dd>
              </div>
              <div>
                <dt className="text-ink-body">1601-EQ item 19{eq?.status && eq.status !== "not_started" ? ` (${eq.status})` : ""}</dt>
                <dd className="tabular-nums text-ink flex items-center gap-1" data-testid="qap-item19">
                  {item19 === null || item19 === undefined ? (
                    <span className="text-danger-700">No saved 1601-EQ</span>
                  ) : (
                    <>
                      {money(item19)}
                      {ties
                        ? <span className="text-success-700 flex items-center gap-0.5"><CheckCircle2 className="w-4 h-4" /> ties</span>
                        : <span className="text-danger-700 flex items-center gap-0.5"><XCircle className="w-4 h-4" /> mismatch</span>}
                    </>
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <section className="border border-ink-line rounded-lg bg-white p-4 mb-4" aria-label="Pre-export checks">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h4 className="text-sm font-medium text-ink">Pre-export checks</h4>
              {hasRowProblems && onGoToEwtRecords && (
                <Button variant="outline" size="sm" onClick={onGoToEwtRecords}>Go to EWT Records</Button>
              )}
            </div>
            <ul>{data.checks.map((c) => <CheckRow key={c.code} check={c} />)}</ul>
          </section>

          <section className="border border-ink-line rounded-lg bg-white p-4 mb-4" aria-label="Preview">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h4 className="text-sm font-medium text-ink">Preview — {rows.length} detail record{rows.length !== 1 ? "s" : ""}</h4>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  icon={Download}
                  disabled={!rows.length}
                  onClick={() => downloadQapPreviewCsv(data, `QAP-preview-Q${quarter}-${year}.csv`)}
                >
                  Download CSV
                </Button>
                <Button
                  size="sm"
                  icon={Download}
                  disabled={!built?.ok}
                  title={built?.ok ? undefined : "Fix the failing checks first"}
                  onClick={() => built?.ok && downloadQapDat(built)}
                >
                  Download DAT
                </Button>
              </div>
            </div>
            {built && !built.ok && failing.length === 0 && (
              <ul role="alert" className="mb-2 text-xs text-danger-700 list-disc ml-4">
                {built.errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            )}
            {built?.ok && <p className="mb-2 text-xs text-ink-body">File: <span className="font-mono">{built.filename}</span></p>}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" aria-label="QAP preview">
                <thead>
                  <tr className="text-xs font-medium text-ink-body uppercase tracking-wider">
                    <th className="text-left py-1 pr-3">Seq</th>
                    <th className="text-left py-1 pr-3">Payee</th>
                    <th className="text-left py-1 pr-3">TIN</th>
                    <th className="text-left py-1 pr-3">ATC</th>
                    <th className="text-right py-1 pr-3">Tax base</th>
                    <th className="text-right py-1 pr-3">Rate</th>
                    <th className="text-right py-1">Tax withheld</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={7} className="py-3 text-center text-ink-body">No EWT rows for this quarter.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.seq} className="border-t border-ink-line">
                      <td className="py-2 pr-3 tabular-nums text-ink">{r.seq}</td>
                      <td className="py-2 pr-3 text-ink">{r.payee}</td>
                      <td className="py-2 pr-3 tabular-nums text-ink">{tinLabel(r.tin9)}{r.tin9 ? `-${r.branchCode}` : ""}</td>
                      <td className="py-2 pr-3 text-ink">{r.atcCode}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-ink">{money(r.taxBaseCentavos)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-ink">{rateLabel(r.rateBasisPoints)}</td>
                      <td className="py-2 text-right tabular-nums text-ink">{money(r.taxWithheldCentavos)}</td>
                    </tr>
                  ))}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t border-ink-line font-medium">
                      <td colSpan={4} className="py-2 pr-3 text-ink">Total</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-ink">{money(data.control.totalTaxBaseCentavos)}</td>
                      <td />
                      <td className="py-2 text-right tabular-nums text-ink">{money(totalWithheld)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default QapDatTab;
